import {
  LineItemApiCreateRequest,
  MgmtFeeLineItemApiCreateRequest,
  StandardLineItemApiCreateRequest,
  ZeroDollarLineItemApiCreateRequest
} from 'shared/src/api/line-item-api-types';
import {
  CommonReducerState,
  MgmtFeeFormState,
  StandardFormState,
  ZeroDollarFormState
} from '@/features/line-items/line-item-form/common/form-reducer';
import type { core, ZodIssue } from 'zod';
import { isEqual } from 'lodash';
import {
  Option,
  platformsForTactics,
  tacticsForChannel,
  unitPriceTypesForTactic
} from 'shared/src/domain/line-item/line-item-channels-filters';
import {
  channelById,
  tacticById,
  unitPriceTypeById
} from 'shared/src/domain/line-item/line-item-channels';
import {
  daysBetween,
  isWithinRange,
  today as getToday
} from 'shared/src/date-string-utils/date-utils';
import {
  PacingBlockClient,
  toMgmtFeeApiRequest,
  toStandardApiRequest,
  toZeroDollarApiRequest
} from '@/features/line-items/line-item-form/common/form-state';
import {
  type PacingValidatorExisting,
  validateLineItemPacing
} from 'shared/src/domain/line-item/pacing-validator';
import { toValidatorUpdate } from 'shared/src/domain/line-item/pacing-validator-adapter';

export type PacingBlockRow = {
  index: number;
  block: PacingBlockClient;
  dailyBudget: number | undefined;
  days: number | undefined;
  spent: number | undefined;
};

export type PriceAllocationSummary = {
  allocated: number | undefined;
  total: number;
  pacingAmount: number | undefined;
};

export type ValidationResult = {
  fieldErrors: Record<string, string>;
  formErrors: string[];
  fieldWarnings: Record<string, string>;
  formWarnings: string[];
};

export function hasValidationErrors(result: ValidationResult): boolean {
  return Object.keys(result.fieldErrors).length > 0 || result.formErrors.length > 0;
}

export function hasValidationWarnings(result: ValidationResult): boolean {
  return Object.keys(result.fieldWarnings).length > 0 || result.formWarnings.length > 0;
}

const STANDARD_FIELD_KEYS = new Set(Object.keys(StandardLineItemApiCreateRequest.shape));
const MGMT_FEE_FIELD_KEYS = new Set(Object.keys(MgmtFeeLineItemApiCreateRequest.shape));
const ZERO_DOLLAR_FIELD_KEYS = new Set(Object.keys(ZeroDollarLineItemApiCreateRequest.shape));

export function selectStandardValidationErrors(state: StandardFormState): ValidationResult {
  const result = StandardLineItemApiCreateRequest.safeParse(toStandardApiRequest(state));
  if (!result.success) return classifyZodIssues(issuesOf(result), STANDARD_FIELD_KEYS);
  return classifyPacingIssues(runPacingValidator(state, result.data), STANDARD_FIELD_KEYS);
}

export function selectMgmtFeeValidationErrors(state: MgmtFeeFormState): ValidationResult {
  const result = MgmtFeeLineItemApiCreateRequest.safeParse(toMgmtFeeApiRequest(state));
  if (!result.success) return classifyZodIssues(issuesOf(result), MGMT_FEE_FIELD_KEYS);
  return classifyPacingIssues(runPacingValidator(state, result.data), MGMT_FEE_FIELD_KEYS);
}

export function selectZeroDollarValidationErrors(state: ZeroDollarFormState): ValidationResult {
  const result = ZeroDollarLineItemApiCreateRequest.safeParse(toZeroDollarApiRequest(state));
  if (!result.success) return classifyZodIssues(issuesOf(result), ZERO_DOLLAR_FIELD_KEYS);
  return classifyPacingIssues(runPacingValidator(state, result.data), ZERO_DOLLAR_FIELD_KEYS);
}

export function isDirtyStandard(state: StandardFormState): boolean {
  return !isEqual(state.formData, state.existing.data);
}

export function isDirtyMgmtFee(state: MgmtFeeFormState): boolean {
  return !isEqual(state.formData, state.existing.data);
}

export function isDirtyZeroDollar(state: ZeroDollarFormState): boolean {
  return !isEqual(state.formData, state.existing.data);
}

export type PriceAllocation = {
  allocated: number | undefined;
  total: number;
};

export function selectPriceAllocation(
  state: StandardFormState | MgmtFeeFormState
): PriceAllocation {
  const { proposal_price, allocated_price } = state.proposalInfo;
  const { price } = state.formData;
  const total = proposal_price;
  if (state.mode === 'create') {
    return { allocated: allocated_price + (price ?? 0), total };
  }
  if (price === undefined || state.existing.data.price === undefined) {
    return { allocated: undefined, total };
  }
  return { allocated: allocated_price + (price - state.existing.data.price), total };
}

export function selectZeroDollarBudget(state: ZeroDollarFormState): number | undefined {
  const { planned_unit_cost, estimated_units } = state.formData;
  return planned_unit_cost != null && estimated_units != null
    ? planned_unit_cost * estimated_units
    : undefined;
}

export function allocationSummary(
  state: CommonReducerState,
  budget: number | undefined
): PriceAllocationSummary {
  const { pacing_blocks } = state.formData;
  if (budget === undefined) {
    return { allocated: undefined, total: budget ?? 0, pacingAmount: undefined };
  }
  const allocated = pacing_blocks.reduce<number>((sum, b) => sum + (b.budget ?? 0), 0);
  return { allocated, total: budget, pacingAmount: budget };
}

export function selectAvailableTactics(state: CommonReducerState): Option[] {
  if (state.formData.channel_id == null) return [];
  return tacticsForChannel(state.channels, state.formData.channel_id);
}

export function selectAvailableUnitPriceTypes(state: CommonReducerState): Option[] {
  if (state.formData.tactic_id == null) return [];
  return unitPriceTypesForTactic(state.channels, state.formData.tactic_id);
}

export function selectAvailablePlatforms(state: CommonReducerState): Option[] {
  if (state.formData.tactic_id == null) return [];
  return platformsForTactics(state.channels, state.formData.tactic_id);
}

export function selectChannelName(state: CommonReducerState): string | undefined {
  const { channel_id } = state.formData;
  return !channel_id ? undefined : channelById(state.channels, channel_id).name;
}

export function selectTacticName(state: CommonReducerState): string | undefined {
  const { channel_id, tactic_id } = state.formData;
  return !channel_id || !tactic_id ? undefined : tacticById(state.channels, tactic_id).name;
}

export function selectUnitPriceTypeName(state: CommonReducerState): string | undefined {
  const { channel_id, tactic_id, unit_price_type_id } = state.formData;
  return !channel_id || !tactic_id || !unit_price_type_id
    ? undefined
    : unitPriceTypeById(state.channels, unit_price_type_id).name;
}

export function selectTotalSpend(state: CommonReducerState): number {
  return state.dailyBudgetSpent.reduce((sum, row) => sum + (row.amount ?? 0), 0);
}

export function selectCurrentBlockIndex(state: CommonReducerState): number | undefined {
  const today = getToday();
  const index = state.formData.pacing_blocks.findIndex(block => isWithinRange(today, block.dates));
  return index === -1 ? undefined : index;
}

export function selectIsLocked(state: CommonReducerState): boolean {
  return state.execution_status !== 'no_execution';
}

export function selectDisplayCPM(
  state: CommonReducerState,
  perUnitValue: number | undefined
): number | undefined {
  if (perUnitValue === undefined) return undefined;
  return selectUnitPriceTypeName(state) === 'CPM' ? perUnitValue * 1000 : perUnitValue;
}

export function selectPacingBlockRows(state: CommonReducerState): PacingBlockRow[] {
  return state.formData.pacing_blocks.map((block, index) => {
    const days =
      block.dates.from && block.dates.to
        ? daysBetween(block.dates.from, block.dates.to)
        : undefined;
    const dailyBudget =
      days === undefined || days <= 0 || block.budget === undefined
        ? undefined
        : block.budget / days;
    const spent = selectBlockSpent(state, index);
    return { index, block, dailyBudget, days, spent };
  });
}

export function selectBlockSpent(state: CommonReducerState, index: number): number | undefined {
  const block = state.formData.pacing_blocks[index];
  if (!block) return undefined;
  if (state.dailyBudgetSpent == null) return undefined;
  return state.dailyBudgetSpent.reduce((sum, row) => {
    const day = row.day;
    return day >= block.dates.from && day <= block.dates.to ? sum + (row.amount ?? 0) : sum;
  }, 0);
}

export function issuesOf(parsed: { error: { issues: readonly ZodIssue[] } }): ZodIssue[] {
  return parsed.error.issues as ZodIssue[];
}

export type ZodIssueLike = Pick<core.$ZodIssue, 'path' | 'message'>;

export function zodIssuesToFieldErrors(
  issues: readonly ZodIssueLike[],
  allowedPaths?: ReadonlySet<string>
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const issue of issues) {
    if (!issue.path || issue.path.length === 0) continue;
    const key = issue.path.map(String).join('.');
    if (allowedPaths && !allowedPaths.has(key)) continue;
    if (result[key] !== undefined) continue;
    result[key] = issue.message;
  }
  return result;
}

function classifyZodIssues(
  issues: readonly ZodIssueLike[],
  fieldKeys: ReadonlySet<string>
): ValidationResult {
  // Zod issues are always errors; no warning lane for client-side request shape failures.
  return classifyPacingIssues(
    issues.map(i => ({
      path: zodPathToString(i.path ?? []),
      message: i.message,
      severity: 'error' as const
    })),
    fieldKeys
  );
}

function zodPathToString(path: readonly PropertyKey[]): string {
  if (path.length === 0) return '';
  let out = String(path[0]);
  for (let i = 1; i < path.length; i += 1) {
    const seg = path[i];
    out += typeof seg === 'number' ? `[${seg}]` : `.${String(seg)}`;
  }
  return out;
}

type ClassifiableIssue = {
  path: string;
  message: string;
  severity?: 'error' | 'warning';
};

function classifyPacingIssues(
  issues: readonly ClassifiableIssue[],
  fieldKeys: ReadonlySet<string>
): ValidationResult {
  const fieldErrors: Record<string, string> = {};
  const formErrors: string[] = [];
  const fieldWarnings: Record<string, string> = {};
  const formWarnings: string[] = [];
  for (const issue of issues) {
    const isWarning = issue.severity === 'warning';
    const fieldBucket = isWarning ? fieldWarnings : fieldErrors;
    const formBucket = isWarning ? formWarnings : formErrors;
    const blockKey = normalizePacingBlockPath(issue.path);
    if (blockKey !== undefined) {
      if (fieldBucket[blockKey] === undefined) fieldBucket[blockKey] = issue.message;
      continue;
    }
    // Pacing-validator paths can be top-level ('price') or nested under a top-level field
    // ('dates.from', 'dates.to'). Route nested paths to their top-level field key so they
    // render next to the matching input (e.g., the dates picker) instead of in the form-
    // level errors area.
    const head = issue.path.split('.')[0];
    if (fieldKeys.has(head)) {
      if (fieldBucket[head] === undefined) fieldBucket[head] = issue.message;
    } else {
      formBucket.push(issue.message);
    }
  }
  return { fieldErrors, formErrors, fieldWarnings, formWarnings };
}

const PACING_BLOCK_PATH_RE = /^pacing_details\[(\d+)\](?:\.(budget|dates))?$/;

function normalizePacingBlockPath(path: string): string | undefined {
  const match = PACING_BLOCK_PATH_RE.exec(path);
  if (!match) return undefined;
  const index = match[1];
  const field = match[2];
  return field ? `pacing_blocks.${index}.${field}` : `pacing_blocks.${index}`;
}

function runPacingValidator(
  state: StandardFormState | MgmtFeeFormState | ZeroDollarFormState,
  request: LineItemApiCreateRequest
): readonly ClassifiableIssue[] {
  const result = validateLineItemPacing(
    toExisting(state),
    toValidatorUpdate(request),
    state.dailyBudgetSpent
  );
  return result.issues;
}

function toExisting(
  state: StandardFormState | MgmtFeeFormState | ZeroDollarFormState
): PacingValidatorExisting | null {
  if (!selectIsLocked(state)) return null;
  const data = state.existing.data;
  return {
    execution_status: state.execution_status,
    dates: data.dates,
    pacing_type: data.pacing_type,
    pacing_details: data.pacing_blocks.map(b => ({ dates: b.dates, budget: b.budget }))
  };
}
